import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import BrandLockup from "@/components/BrandLockup";
import PublicMarketingThemeControl, { usePublicMarketingTheme } from "@/components/PublicMarketingThemeControl";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import { useProgressiveSectionReveal } from "@/lib/progressiveSectionReveal";

function track(source, section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function ProductPageShell({ source, children }) {
  const productPageRef = useRef(null);
  const { preference: marketingThemePreference, resolvedTheme: marketingResolvedTheme, setPreference: setMarketingThemePreference } = usePublicMarketingTheme();
  useProgressiveSectionReveal(
    productPageRef,
    "main > section:not(:nth-of-type(2)), .product-feature-shell-final"
  );
  useProgressiveSectionReveal(
    productPageRef,
    "main > section:nth-of-type(2)",
    { rootMargin: "0px 0px -24% 0px", threshold: 0.2 }
  );

  const marketingThemeClass = marketingResolvedTheme === "dark"
    ? "public-fixed-dark dark marketing-theme-dark"
    : "marketing-theme-light";

  return (
    <div
      ref={productPageRef}
      data-marketing-theme={marketingResolvedTheme}
      className={`${marketingThemeClass} public-marketing-theme marketing-shell product-feature-page min-h-screen`}
    >
      <header className="marketing-header relative z-30 border-b px-5 py-4 backdrop-blur-xl sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <Link to="/" className="brand-lockup-link" aria-label="Callsect home">
            <BrandLockup className="brand-lockup--landing" variant="auto" />
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <PublicMarketingThemeControl value={marketingThemePreference} onChange={setMarketingThemePreference} />
            <Link
              to="/login?mode=register"
              onClick={() => track(source, "header", "sign_up_free")}
              className="marketing-header-signup inline-flex items-center rounded-lg border px-4 py-2 text-sm font-semibold transition"
            >
              Sign up free
            </Link>
            <Link
              to="/"
              onClick={() => track(source, "header", "back_to_homepage_mobile")}
              className="product-feature-header__mobile-back"
              aria-label="Back to Callsect homepage"
            >
              <ArrowLeft size={15} weight="bold" aria-hidden="true" />
            </Link>
            <Link
              to="/"
              onClick={() => track(source, "header", "back_to_homepage")}
              className="product-feature-header__home-link hidden items-center gap-2 px-3 py-2 text-sm font-medium marketing-text-secondary transition sm:inline-flex"
            >
              <ArrowLeft size={14} weight="bold" />
              Back to homepage
            </Link>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <section className="product-feature-shell-final px-5 py-16 sm:px-8 sm:py-20">
        <div className="product-feature-final product-feature-final--no-kicker mx-auto max-w-5xl">
          <h2>Finish the call. Callsect takes it from there.</h2>
          <p className="marketing-lead-copy">See the real workflow with synthetic customer-call data, or sign up free and use Callsect with your own conversations.</p>
          <div>
            <Link
              to="/login?mode=register"
              onClick={() => track(source, "final_cta", "sign_up_free")}
              className="product-feature-final__primary"
            >
              Sign up free
            </Link>
            <Link
              to="/demo"
              onClick={() => track(source, "final_cta", "see_demo")}
              className="product-feature-final__secondary"
            >
              See Demo
              <ArrowRight size={17} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="marketing-footer border-t px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <BrandLockup className="brand-lockup--footer" variant="auto" />
            <span>© 2026 Callsect</span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/data-handling">Data handling</Link>
            <Link to="/about">About</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
